/** Supported functionality and legacy storage defaults; availability is not rollout readiness. */
export const staffPermissionCatalog = {
  canChangeAppointmentStatus: {
    group: "Calendar", label: "Can change status of appointments",
    description: "Change the status of visible appointments. Cancelling or restoring an appointment also requires permission to edit it; checkout requires checkout access.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canChargeNoShowFees: {
    group: "Calendar", label: "Can charge and refund no-show & late-cancel fees",
    description: "Charge, waive and refund no-show and late-cancellation fees on cards clients saved for No-show protection.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canBookOwnAppointments: {
    group: "Calendar", label: "Can book and change own appointments",
    description: "Create, edit, move, cancel and delete appointments assigned to this staff member.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canViewOthersCalendars: {
    group: "Calendar", label: "Can view other people's calendar",
    description: "View other team members’ calendars, appointments and time blocks.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canBookOthersAppointments: {
    group: "Calendar", label: "Can book and change other people's appointments",
    description: "Create and change appointments assigned to other team members whose calendars this staff member can view.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canManageOwnTimeBlocks: {
    group: "Calendar", label: "Can create and change own time blocks",
    description: "Create, change and delete own time blocks, including repeating blocks.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canManageOthersTimeBlocks: {
    group: "Calendar", label: "Can create and change other people's time blocks",
    description: "Create, change and delete time blocks on other team members’ visible calendars.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canCreateChangeOthersIntervals: {
    group: "Calendar", label: "Can create and change other people's work schedule intervals",
    description: "Change other team members’ recurring work schedules and individual shifts.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canManageWaitlist: {
    group: "Calendar", label: "Can manage waitlist",
    description: "Add, update and remove waitlist entries. Booking from the waitlist also requires appointment booking permissions.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canViewClientLastNames: {
    group: "Clients", label: "Can view clients' last names",
    description: "Show client last names. Access to the Clients list also requires permission to view contact details.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canAccessClientContactDetails: {
    group: "Clients", label: "Can access clients' contact details",
    description: "Show client phone numbers, email addresses and addresses. Access to the Clients list also requires permission to view last names.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canDeleteClients: {
    group: "Clients", label: "Can delete and merge clients",
    description: "Delete and merge accessible clients. This does not grant access to additional clients.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canManuallyAdjustClientAccountBalance: {
    group: "Clients", label: "Can manually adjust client account balances",
    description: "Manually increase or decrease an accessible client’s account balance. This does not grant Clients list or checkout access.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canAddClientReferrers: {
    group: "Clients", label: "Can add who referred a client",
    description: "Record who sent a new client, for example from a referral card or “Lan sent me”, within 14 days of their first visit. The referrer earns their friend reward and gets a text. Each one is noted on the client’s timeline.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canTagClientCreators: {
    group: "Clients", label: "Can tag which creator sent a client",
    description: "On today’s visit, record that a new walk-in came from a creator’s video, for example when they say “MAI15”. The client gets the salon’s New Client Offer for that visit. Only works for appointments this staff member can see.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canViewIndividualSalesOnCalendar: {
    group: "Sales", label: "Can view individual sales on calendar",
    description: "View sales attached to visible appointments. This does not grant Sales list access or permission to modify sales.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canViewListOfOwnSales: {
    group: "Sales", label: "Can view list of own sales",
    description: "View sales with items assigned to this staff member. This does not allow modifying sales.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canViewAllSales: {
    group: "Sales", label: "Can view all sales",
    description: "View all salon sales and their details. This does not allow modifying sales.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canSellAtCheckout: {
    group: "Sales", label: "Can open a checkout and modify sales",
    description: "Open checkout, change open sales, accept payments, complete sales and discard open sales.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canSetNonRetailProducts: {
    group: "Sales", label: "Can sell non retail products",
    description: "Sell products marked as non-retail during checkout.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canRefundOpenAndClosedSales: {
    group: "Sales", label: "Can refund sales (open and closed)",
    description: "Sale refunds are not available.",
    available: false, unavailableReason: "Sale refunds are not available.", normalDefault: false,
  },
  canRefundOpenSales: {
    group: "Sales", label: "Can refund open sales",
    description: "Refunding payments on open sales is not available.",
    available: false, unavailableReason: "Refunding payments on open sales is not available.", normalDefault: false,
  },
  canViewIndividualConversations: {
    group: "Messages", label: "Can view individual conversations",
    description: "View conversations for accessible clients. This does not grant access to the full Inbox or allow sending messages.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canViewAllConversations: {
    group: "Messages", label: "Can view all conversations",
    description: "View the Inbox and all salon conversations. This does not allow sending messages.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canSendMessages: {
    group: "Messages", label: "Can send messages",
    description: "Send messages and add notes in conversations this staff member can view.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canAccessCompanyReports: {
    group: "Reports", label: "Can access company reports",
    description: "View company reports. Marketing analytics also requires a Campaigns permission.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canManageProducts: {
    group: "Products", label: "Can manage products",
    description: "Create, change and deactivate products and categories, and manually adjust inventory.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canManageGiftCards: {
    group: "Gift Cards", label: "Can manage gift cards",
    description: "Create gift cards and change their details or balances. Gift card settings and promotions require an administrator; selling and redeeming cards uses checkout access.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canManagePackages: {
    group: "Packages", label: "Can manage packages",
    description: "Client package management is not available.",
    available: false, unavailableReason: "Client package management is not available.", normalDefault: false,
  },
  canManagePackageSetup: {
    group: "Packages", label: "Can manage package setup",
    description: "Package setup is not available.",
    available: false, unavailableReason: "Package setup is not available.", normalDefault: false,
  },
  canManageMemberships: {
    group: "Memberships", label: "Can manage memberships",
    description: "Membership management is not available.",
    available: false, unavailableReason: "Membership management is not available.", normalDefault: false,
  },
  canRequireMembershipAgreements: {
    group: "Memberships", label: "Can bypass membership agreements",
    description: "Membership agreements are not available.",
    available: false, unavailableReason: "Membership agreements are not available.", normalDefault: false,
  },
  canManageMembershipPlans: {
    group: "Memberships", label: "Can manage membership plans",
    description: "Membership plan setup is not available.",
    available: false, unavailableReason: "Membership plan setup is not available.", normalDefault: false,
  },
  canManageCashDrawer: {
    group: "Cash Drawer", label: "Can manage cash drawer",
    description: "Cash drawer counts and manual cash entries are not available.",
    available: false, unavailableReason: "Cash drawer counts and manual cash entries are not available.", normalDefault: false,
  },
  canViewTimeClockApp: {
    group: "Time Clock", label: "Can view Time Clock app",
    description: "Time Clock is not available. Turn Tracker sign-in does not track attendance.",
    available: false, unavailableReason: "Time Clock is not available. Turn Tracker sign-in does not track attendance.", normalDefault: false,
  },
  canManageTimeCodes: {
    group: "Time Clock", label: "Can manage time cards",
    description: "Time card management is not available.",
    available: false, unavailableReason: "Time card management is not available.", normalDefault: false,
  },
  canViewPayrollApp: {
    group: "Payroll", label: "Can view Payroll app",
    description: "Payroll reports are not available.",
    available: false, unavailableReason: "Payroll reports are not available.", normalDefault: false,
  },
  canManagePayrollAdjustments: {
    group: "Payroll", label: "Can manage payroll adjustments",
    description: "Payroll adjustments are not available.",
    available: false, unavailableReason: "Payroll adjustments are not available.", normalDefault: false,
  },
  canViewIndividualFormSubmissions: {
    group: "Forms", label: "Can view individual form submissions",
    description: "Appointment form submissions are not available.",
    available: false, unavailableReason: "Appointment form submissions are not available.", normalDefault: false,
  },
  canViewAllFormSubmissions: {
    group: "Forms", label: "Can view all form submissions",
    description: "The Forms app is not available.",
    available: false, unavailableReason: "The Forms app is not available.", normalDefault: false,
  },
  canManageFormTemplates: {
    group: "Forms", label: "Can manage form templates",
    description: "Appointment form template management is not available.",
    available: false, unavailableReason: "Appointment form template management is not available.", normalDefault: false,
  },
  canManageResources: {
    group: "Resources", label: "Can manage resources",
    description: "Resource management is not available.",
    available: false, unavailableReason: "Resource management is not available.", normalDefault: false,
  },
  canViewResourcesOnCalendar: {
    group: "Resources", label: "Can view resources on calendar",
    description: "Resource calendars are not available.",
    available: false, unavailableReason: "Resource calendars are not available.", normalDefault: false,
  },
  canEditCampaigns: {
    group: "Campaigns", label: "Can edit campaigns",
    description: "Create and edit campaigns, landing pages and bulk message drafts.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canSendCampaigns: {
    group: "Campaigns", label: "Can send campaigns",
    description: "Send bulk messages and manage live campaign activity.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canManageFlows: {
    group: "Flows", label: "Can manage campaign automations",
    description: "Manage campaign automations.",
    available: true, unavailableReason: null, normalDefault: false,
  },
  canManageOffers: {
    group: "Offers", label: "Can manage offers",
    description: "Standalone offer management is not available. Campaign offer text is managed with campaign editing.",
    available: false, unavailableReason: "Standalone offer management is not available. Campaign offer text is managed with campaign editing.", normalDefault: false,
  },
  canManageStaffMembers: {
    group: "Setup", label: "Can manage staff members",
    description: "Edit existing staff details, service assignments and work schedules. Accounts, invitations, roles, permissions and compensation require an administrator.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canManageServiceSettings: {
    group: "Setup", label: "Can manage service settings",
    description: "Manage services, categories, options, staff assignments and service booking settings. Compensation and deductions require an administrator.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canManageSalonSettings: {
    group: "Setup", label: "Can manage salon settings",
    description: "Change business hours, calendar and appointment settings, check-in, online booking, group booking and Turns settings. Business details, branding, phone numbers, payroll, integrations and automated messages require an administrator.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canManagePaymentAccounts: {
    group: "Setup", label: "Can manage payment accounts",
    description: "Payment account administration is not available.",
    available: false, unavailableReason: "Payment account administration is not available.", normalDefault: false,
  },
  canChangeOwnWorkSchedule: {
    group: "Profile", label: "Can change own work schedule",
    description: "Change own recurring work schedule and individual shifts.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canChangePersonalInformation: {
    group: "Profile", label: "Can change personal information",
    description: "Change own profile photo and personal details. This does not allow changing roles or permissions.",
    available: true, unavailableReason: null, normalDefault: true,
  },
  canViewOwnDailySummaryEmail: {
    group: "Profile", label: "Can view own daily/weekly totals",
    description: "Daily and weekly personal totals are not available.",
    available: false, unavailableReason: "Daily and weekly personal totals are not available.", normalDefault: false,
  },
  canChangeOwnServiceAssignments: {
    group: "Profile", label: "Can change own service assignments",
    description: "Change own service assignments, prices and durations. Compensation requires an administrator.",
    available: true, unavailableReason: null, normalDefault: true,
  },
} as const;

export type StaffPermissionKey = keyof typeof staffPermissionCatalog;
export type EffectiveStaffPermissions = Record<StaffPermissionKey, boolean>;
export const staffPermissionKeys = Object.keys(staffPermissionCatalog) as StaffPermissionKey[];

// Preserve persisted defaults. Effective resolution masks unavailable legacy grants.
export const normalPermissionsDefaults = Object.fromEntries(
  staffPermissionKeys.map((key) => [key, staffPermissionCatalog[key].normalDefault]),
) as EffectiveStaffPermissions;
export const adminPermissionsDefaults = Object.fromEntries(
  staffPermissionKeys.map((key) => [key, true]),
) as EffectiveStaffPermissions;

export function resolveEffectiveStaffPermissions(
  role: 'ADMIN' | 'NORMAL',
  stored?: Partial<EffectiveStaffPermissions> | null,
): EffectiveStaffPermissions {
  return Object.fromEntries(staffPermissionKeys.map((key) => [
    key,
    staffPermissionCatalog[key].available &&
      (role === 'ADMIN' || (stored?.[key] ?? normalPermissionsDefaults[key])),
  ])) as EffectiveStaffPermissions;
}

export function deriveStaffCapabilities(
  role: 'ADMIN' | 'NORMAL',
  permissions: EffectiveStaffPermissions,
) {
  return {
    canManageSalon: role === 'ADMIN',
    canAccessClients: permissions.canViewClientLastNames && permissions.canAccessClientContactDetails,
    canAccessSales: permissions.canViewAllSales || permissions.canViewListOfOwnSales,
    canAccessInbox: permissions.canViewAllConversations,
    canAccessReports: permissions.canAccessCompanyReports,
  };
}

/** The hint on a grant that only works together with "view other people's calendar". */
export const NEEDS_OTHERS_CALENDARS_EXPLANATION = 'To use this permission, also enable viewing other people’s calendars.';

/** Explain effective action scope without modifying the stored grants. */
export function getStaffPermissionExplanation(
  key: StaffPermissionKey,
  permissions: Partial<EffectiveStaffPermissions>,
): string | undefined {
  if ((key === 'canBookOthersAppointments' || key === 'canManageOthersTimeBlocks') &&
      permissions[key] && !permissions.canViewOthersCalendars) {
    return NEEDS_OTHERS_CALENDARS_EXPLANATION;
  }
  if (key === 'canAddClientReferrers' && permissions[key] &&
      !(permissions.canViewClientLastNames && permissions.canAccessClientContactDetails)) {
    return 'To add referrers, also enable viewing clients’ last names and contact details.';
  }
  if (key === 'canSetNonRetailProducts' && permissions[key] && !permissions.canSellAtCheckout) {
    return 'To sell non-retail products, also enable checkout access.';
  }
  if (key === 'canSendMessages' && permissions[key] &&
      !permissions.canViewIndividualConversations && !permissions.canViewAllConversations) {
    return 'To send messages, also enable individual conversation or all-conversation viewing.';
  }
  if (key === 'canViewIndividualConversations' && permissions.canViewAllConversations) {
    return 'All-conversation viewing already includes individual conversations.';
  }
  if (key === 'canViewListOfOwnSales' && permissions.canViewAllSales) {
    return 'All-sales viewing already includes this staff member’s own sales.';
  }
  if ((key === 'canChangeOwnWorkSchedule' || key === 'canCreateChangeOthersIntervals' ||
       key === 'canChangePersonalInformation') && permissions.canManageStaffMembers) {
    return 'Staff management already includes this access.';
  }
  if (key === 'canChangeOwnServiceAssignments' &&
      (permissions.canManageStaffMembers || permissions.canManageServiceSettings)) {
    return 'Staff management or service management already includes this access.';
  }
  return undefined;
}
